/** Assistant chat page: streaming answer, stop generation, friend-chat styling. */
import { useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../components/ChatMessage';
import { Composer } from '../components/Composer';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { StatusBanner } from '../components/StatusBanner';
import { conversationLabel } from '../lib/chat';
import { useApp } from '../state/AppState';
import type { Route } from '../components/NavBar';

const QUICK_PROMPTS = ['最近怎么样？', '帮我看看网络学堂作业', '我有未读邮件吗？'];

export function ChatPage({ onNavigate }: { onNavigate(route: Route): void }) {
  const {
    activeConversation,
    conversations,
    isStreaming,
    newConversation,
    selectConversation,
    deleteConversation,
    sendChat,
    paired,
    settings,
  } = useApp();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const turns = activeConversation.turns;

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  return (
    <div className="flex h-full flex-col">
      <StatusBanner />
      <div className="flex items-center justify-between gap-2 px-4 pb-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={newConversation}
            className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-200"
          >
            <Icon name="plus" className="h-3.5 w-3.5" />
            新聊天
          </button>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={isStreaming}
            aria-label="删除当前聊天"
            title={isStreaming ? '对方回复中不可删除聊天' : '删除当前聊天'}
            className="flex items-center rounded-full border border-white/10 bg-white/5 p-1.5 text-slate-400 disabled:opacity-40"
          >
            <Icon name="trash" className="h-3.5 w-3.5" />
          </button>
        </div>
        <select
          value={activeConversation.id}
          onChange={(event) => selectConversation(event.target.value)}
          className="max-w-[55%] rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-300 outline-none"
          aria-label="切换聊天"
        >
          {conversations.map((conversation) => (
            <option key={conversation.id} value={conversation.id}>
              {conversationLabel(conversation)}
            </option>
          ))}
        </select>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-3 pb-3">
        {turns.length === 0 ? (
          <div className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-500/15 to-transparent p-5">
            <div className="flex items-center gap-2 text-indigo-200">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-semibold text-white">
                L
              </span>
              <h2 className="text-sm font-semibold">Luminis</h2>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-slate-400">
              {settings.demoMode
                ? '嗨，我是 Luminis。今天想聊点什么？'
                : '嗨，我是 Luminis。今天想聊点什么？需要我做事时，直接说就行。'}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => {
                    if (!paired) {
                      onNavigate('pair');
                      return;
                    }
                    sendChat(prompt);
                  }}
                  className="rounded-full border border-indigo-400/30 bg-indigo-400/10 px-3 py-1.5 text-[11px] text-indigo-100"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          turns.map((turn, index) => (
            <ChatMessage
              key={turn.id}
              turn={turn}
              isLast={index === turns.length - 1 && isStreaming}
            />
          ))
        )}
      </div>

      <Composer />

      <ConfirmDialog
        open={confirmDelete}
        title="删除当前聊天"
        confirmLabel="删除"
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          deleteConversation(activeConversation.id);
        }}
      >
        <p>将删除「{conversationLabel(activeConversation)}」的全部本地聊天记录（不可恢复）。</p>
      </ConfirmDialog>
    </div>
  );
}
